// frontend/src/components/GlobalOverlays.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-04
// Updated:      2026-09-23
//
// Description:
//   Mounts global creation overlays for authenticated app pages (but not on
//   excluded routes). Provides stable event bridges for Job, Quote,
//   Opportunity, Customer and Contact creation. Existing forms are reused with
//   tenant-safe APIs; the browser supplies no tenant or user identity fields.
//
// Usage from any page (e.g. calendar):
//   window.__GJCF_PREFILL?.({ starts_at: ISOString, ends_at: ISOString, resource_id: 2 })
//
// Fallback if the function isn’t ready yet:
//   window.dispatchEvent(new CustomEvent("calendar:prefill-job", { detail: payload }))

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import CreateContactModal from "./CreateContactModal";
import CustomerFormModal from "./CustomerFormModal";
import GlobalJobCreateFlyout from "./GlobalJobCreateFlyout"; // ← IMPORTANT: mount the correct flyout
import OpportunityCreateModal from "./OpportunityCreateModal";
import QuoteCreateModal from "./QuoteCreateModal";

const EXCLUDED = new Set([
  "/dashboard/system",
  "/dashboard/widgets",
  "/dashboard/workflows",
]);
const BUILD_TAG = "GlobalOverlays-2026-09-23-PWA003-CREATES";
const CONTACT_CREATE_EVENT = "vision:open-contact-create";
const CUSTOMER_CREATE_EVENT = "vision:open-customer-create";
const OPPORTUNITY_CREATE_EVENT = "vision:open-opportunity-create";
const QUOTE_CREATE_EVENT = "vision:open-quote-create";

function listFrom(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

async function fetchAllCustomers() {
  const rows = [];
  const limit = 200;
  let offset = 0;

  for (let page = 0; page < 50; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await axiosInstance.get("/customers", {
      baseURL: "/api",
      params: { limit, offset },
    });
    const pageRows = listFrom(response?.data);
    rows.push(...pageRows);

    const total = Number(response?.data?.total);
    const pageLimit = Number(response?.data?.limit) || limit;

    if (!Number.isFinite(total) || total <= 0) break;
    offset += pageLimit;
    if (rows.length >= total || pageRows.length === 0) break;
  }

  return rows;
}

function customerLabel(customer) {
  return String(
    customer?.name
      || customer?.company_name
      || customer?.customer_name
      || customer?.client_name
      || `Customer #${customer?.id}`
  ).trim();
}

export default function GlobalOverlays() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [contactOpen, setContactOpen] = useState(false);
  const [contactCustomerId, setContactCustomerId] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [opportunityOpen, setOpportunityOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [customerRows, setCustomerRows] = useState([]);

  const shouldRender = !!user && !EXCLUDED.has(pathname);
  const customerMap = useMemo(
    () =>
      customerRows.reduce((result, customer) => {
        const id = Number(customer?.id);
        if (Number.isFinite(id) && id > 0) {
          result[id] = customerLabel(customer);
        }
        return result;
      }, {}),
    [customerRows]
  );

  const loadCustomers = useCallback(async () => {
    try {
      const rows = await fetchAllCustomers();
      setCustomerRows(rows);
    } catch (error) {
      setCustomerRows([]);
      console.error(
        "[GlobalOverlays] quote customer bootstrap failed:",
        error?.response?.data || error?.message || error
      );
    }
  }, []);

  useEffect(() => {
    if (!shouldRender) return undefined;

    let active = true;

    fetchAllCustomers()
      .then((rows) => {
        if (active) setCustomerRows(rows);
      })
      .catch((error) => {
        if (!active) return;
        setCustomerRows([]);
        console.error(
          "[GlobalOverlays] quote customer bootstrap failed:",
          error?.response?.data || error?.message || error
        );
      });

    return () => {
      active = false;
    };
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender) return undefined;

    const openQuoteCreate = () => {
      setQuoteOpen(true);
      if (Object.keys(customerMap).length === 0) {
        loadCustomers();
      }
    };

    const dispatchQuoteCreate = () => {
      window.dispatchEvent(new CustomEvent(QUOTE_CREATE_EVENT));
    };

    const openOpportunityCreate = () => {
      setOpportunityOpen(true);
    };

    const dispatchOpportunityCreate = () => {
      window.dispatchEvent(new CustomEvent(OPPORTUNITY_CREATE_EVENT));
    };

    const openCustomerCreate = () => {
      setCustomerOpen(true);
    };

    const dispatchCustomerCreate = () => {
      window.dispatchEvent(new CustomEvent(CUSTOMER_CREATE_EVENT));
    };

    const openContactCreate = (event) => {
      const requestedCustomerId = Number(
        event?.detail?.customerId || event?.detail?.customer_id
      );

      setContactCustomerId(
        Number.isFinite(requestedCustomerId) && requestedCustomerId > 0
          ? requestedCustomerId
          : null
      );
      setContactOpen(true);

      if (Object.keys(customerMap).length === 0) {
        loadCustomers();
      }
    };

    const dispatchContactCreate = (detail = {}) => {
      window.dispatchEvent(
        new CustomEvent(CONTACT_CREATE_EVENT, { detail })
      );
    };

    // Define once: stable global bridge
    if (typeof window.__GJCF_PREFILL !== "function") {
      window.__GJCF_PREFILL = (payload = {}) => {
        try {
          window.dispatchEvent(new CustomEvent("calendar:prefill-job", { detail: payload }));
        } catch (e) {
          console.warn("[GlobalOverlays] prefill dispatch failed:", e);
        }
      };
    }

    window.addEventListener(QUOTE_CREATE_EVENT, openQuoteCreate);
    window.addEventListener(OPPORTUNITY_CREATE_EVENT, openOpportunityCreate);
    window.addEventListener(CUSTOMER_CREATE_EVENT, openCustomerCreate);
    window.addEventListener(CONTACT_CREATE_EVENT, openContactCreate);
    window.__VISION_OPEN_QUOTE_CREATE = dispatchQuoteCreate;
    window.__VISION_OPEN_OPPORTUNITY_CREATE = dispatchOpportunityCreate;
    window.__VISION_OPEN_CUSTOMER_CREATE = dispatchCustomerCreate;
    window.__VISION_OPEN_CONTACT_CREATE = dispatchContactCreate;
    window.__GLOBAL_OVERLAYS_BUILD = BUILD_TAG;

    return () => {
      window.removeEventListener(QUOTE_CREATE_EVENT, openQuoteCreate);
      window.removeEventListener(
        OPPORTUNITY_CREATE_EVENT,
        openOpportunityCreate
      );
      window.removeEventListener(CUSTOMER_CREATE_EVENT, openCustomerCreate);
      window.removeEventListener(CONTACT_CREATE_EVENT, openContactCreate);
      if (window.__VISION_OPEN_QUOTE_CREATE === dispatchQuoteCreate) {
        delete window.__VISION_OPEN_QUOTE_CREATE;
      }
      if (
        window.__VISION_OPEN_OPPORTUNITY_CREATE
        === dispatchOpportunityCreate
      ) {
        delete window.__VISION_OPEN_OPPORTUNITY_CREATE;
      }
      if (window.__VISION_OPEN_CUSTOMER_CREATE === dispatchCustomerCreate) {
        delete window.__VISION_OPEN_CUSTOMER_CREATE;
      }
      if (window.__VISION_OPEN_CONTACT_CREATE === dispatchContactCreate) {
        delete window.__VISION_OPEN_CONTACT_CREATE;
      }
    };
  }, [customerMap, loadCustomers, shouldRender]);

  const handleContactCreated = useCallback((contact) => {
    window.dispatchEvent(
      new CustomEvent("vision:contact-changed", {
        detail: { action: "created", contact },
      })
    );
  }, []);

  const openContactForCustomer = useCallback((customerId) => {
    const numericCustomerId = Number(customerId);
    if (!Number.isFinite(numericCustomerId) || numericCustomerId <= 0) return;

    setCustomerOpen(false);
    setContactCustomerId(numericCustomerId);
    setContactOpen(true);
  }, []);

  const handleCustomerCreated = useCallback((customer) => {
    const customerId = Number(customer?.id);

    if (Number.isFinite(customerId) && customerId > 0) {
      setCustomerRows((currentRows) => {
        const withoutCreated = currentRows.filter(
          (row) => Number(row?.id) !== customerId
        );
        return [...withoutCreated, customer];
      });
    }

    window.dispatchEvent(
      new CustomEvent("vision:customer-changed", {
        detail: { action: "created", customer },
      })
    );
  }, []);

  const createOpportunity = useCallback(async (payload) => {
    const response = await axiosInstance.post("/api/opportunities", payload);
    const opportunity = response?.data?.opportunity || response?.data;

    window.dispatchEvent(
      new CustomEvent("vision:opportunity-changed", {
        detail: { action: "created", opportunity },
      })
    );

    return opportunity;
  }, []);

  const createQuote = useCallback(async (payload) => {
    const response = await axiosInstance.post("/api/quotes", payload);
    const quote = response?.data?.quote || response?.data;

    window.dispatchEvent(
      new CustomEvent("vision:quote-changed", {
        detail: { action: "created", quote },
      })
    );

    return quote;
  }, []);

  const handleQuoteChanged = useCallback((detail = {}) => {
    window.dispatchEvent(
      new CustomEvent("vision:quote-changed", {
        detail: { action: "updated", ...detail },
      })
    );
  }, []);

  if (!shouldRender) return null;

  return (
    <>
      <GlobalJobCreateFlyout prefillEventName="calendar:prefill-job" />
      {contactOpen && (
        <CreateContactModal
          customerId={contactCustomerId}
          customers={customerRows}
          onClose={() => {
            setContactOpen(false);
            setContactCustomerId(null);
          }}
          onContactCreated={handleContactCreated}
        />
      )}
      <CustomerFormModal
        open={customerOpen}
        onClose={() => setCustomerOpen(false)}
        onCreateContact={openContactForCustomer}
        onCustomerCreated={handleCustomerCreated}
      />
      <OpportunityCreateModal
        open={opportunityOpen}
        onClose={() => setOpportunityOpen(false)}
        onCreate={createOpportunity}
      />
      <QuoteCreateModal
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        onCreate={createQuote}
        onQuoteChanged={handleQuoteChanged}
        customerMap={customerMap}
      />
    </>
  );
}
